/**
 * Reload Loop Breaker Plugin
 *
 * Detects and breaks infinite reload loops caused by service worker updates,
 * particularly on iOS Safari.
 *
 * **Server-side**: Injects inline script into <head> that tracks reloads via
 * sessionStorage. If >2 reloads in 2 minutes, performs nuclear reset (clear
 * all caches, unregister all SWs, reload) and stashes diagnostics.
 *
 * **Client-side**: Reports stashed diagnostics to Sentry after the clean reload.
 *
 * Pattern follows `plugins/early-error-capture.ts`.
 */

declare global {
  interface Window {
    __reloadLoopDetected?: boolean
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // SERVER-SIDE: Inject reload loop detection script into <head>
  if (process.server) {
    injectBreakerScript(nuxtApp)
    return
  }

  // CLIENT-SIDE: Report stashed diagnostics to Sentry
  reportReloadLoop(nuxtApp)
})

/**
 * Server-side: Inject inline <head> script for reload loop detection.
 * Must run before any other JS to catch loops that happen before hydration.
 */
function injectBreakerScript(nuxtApp: any) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- Nuxt app hooks are untyped
  nuxtApp.hooks.hook('app:rendered', () => {
    useHead({
      script: [
        {
          id: 'reload-loop-breaker',
          tagPriority: -10, // Must run before any other JS
          innerHTML: `
(function() {
  try {
    var KEY = '__reload_ts';
    var WINDOW_MS = 120000;
    var THRESHOLD = 2;
    var now = Date.now();

    // Test mode: ?testReloadLoop=true seeds fake timestamps to trigger breaker
    var testMode = window.location.search.indexOf('testReloadLoop=true') !== -1;

    var raw = sessionStorage.getItem(KEY);
    var timestamps = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(timestamps)) timestamps = [];

    // In test mode, inject fake recent timestamps to exceed the threshold
    if (testMode) {
      timestamps = [now - 1000, now - 2000];
      console.warn('[Reload Loop] Test mode — injecting fake timestamps to trigger breaker');
    }

    // Filter to recent timestamps within the 2-minute window
    var recent = [];
    for (var i = 0; i < timestamps.length; i++) {
      if (now - timestamps[i] < WINDOW_MS) recent.push(timestamps[i]);
    }

    if (recent.length >= THRESHOLD) {
      // Reload loop detected — nuclear reset
      window.__reloadLoopDetected = true;
      console.warn('[Reload Loop] Detected ' + (recent.length + 1) + ' reloads in 2 min — clearing caches + SWs');

      // Stash diagnostic context for post-hydration Sentry report
      try {
        sessionStorage.setItem('__reload_loop_report', JSON.stringify({
          count: recent.length + 1,
          timestamps: recent.concat(now),
          url: window.location.href,
          userAgent: navigator.userAgent,
          referrer: document.referrer
        }));
      } catch (e) { /* storage full — diagnostics lost, reset still happens */ }

      // Clear the reload counter
      sessionStorage.removeItem(KEY);

      // Nuclear reset: delete all caches + unregister all SWs, then reload clean
      var cacheP = window.caches
        ? caches.keys().then(function(keys) {
            return Promise.all(keys.map(function(key) { return caches.delete(key); }));
          })
        : Promise.resolve();

      var swP = navigator.serviceWorker
        ? navigator.serviceWorker.getRegistrations().then(function(regs) {
            return Promise.all(regs.map(function(reg) { return reg.unregister(); }));
          })
        : Promise.resolve();

      Promise.all([cacheP, swP]).catch(function() {}).then(function() {
        // Strip testReloadLoop param before reloading to avoid re-triggering test mode
        if (testMode) {
          var url = new URL(window.location.href);
          url.searchParams.delete('testReloadLoop');
          window.location.replace(url.toString());
        } else {
          window.location.reload();
        }
      });
      return;
    }

    // No loop — record this reload timestamp and continue normally
    recent.push(now);
    sessionStorage.setItem(KEY, JSON.stringify(recent));
    console.warn('[Reload Loop] Reload ' + recent.length + '/' + THRESHOLD + ' in window (no action)');
  } catch (e) {
    // sessionStorage unavailable (old Safari private browsing, storage full, etc.)
    // Silently degrade — reload tracking simply doesn't work, which is acceptable.
  }
})();
          `.trim(),
        },
      ],
    })
  })
}

/**
 * Client-side: Read stashed reload loop report from sessionStorage and
 * send it to Sentry. Runs once after the clean reload that follows a
 * nuclear reset.
 */
interface ReloadLoopReport {
  count: number
  timestamps: number[]
  url: string
  userAgent: string
  referrer: string
}

function reportReloadLoop(nuxtApp: any) {
  if (typeof window === 'undefined') return

  let report: ReloadLoopReport | null = null

  try {
    const raw = sessionStorage.getItem('__reload_loop_report')
    if (!raw) return
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- sessionStorage JSON, validated by null check below
    report = JSON.parse(raw)
    sessionStorage.removeItem('__reload_loop_report')
  } catch {
    return
  }

  if (!report) return

  const timestamps = report.timestamps.map((t) => new Date(t).toISOString())

  console.error(
    `[Reload Loop] Detected ${report.count} reloads in 2 minutes — nuclear reset performed`,
    {
      count: report.count,
      timestamps,
      url: report.url,
      userAgent: report.userAgent,
      referrer: report.referrer,
    },
  )

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type nuxtApp properly
  const sentry = nuxtApp.$sentry

  if (sentry) {
    const error = new Error(
      `Reload loop detected: ${report.count} reloads in 2 minutes`,
    )
    error.name = 'ReloadLoopDetected'

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type sentry properly
    sentry.captureException(error, {
      level: 'error',
      tags: {
        'reload-loop': true,
        'error-source': 'reload-loop-breaker',
      },
      extra: {
        reloadCount: report.count,
        timestamps,
        url: report.url,
        userAgent: report.userAgent,
        referrer: report.referrer,
      },
    })
  }
}
